import { CSSProperties, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { GridApi } from 'ag-grid-community'
import { CalendarDateVO } from '../../../../../../../../../domain/value-object/CalendarDateVO'
import {
  AggregateField,
  AggregateTarget,
} from '../../../../../../../../../domain/entity/WbsItemEntity'
import { GanttDisplayUnit } from '../../../../../../../../../domain/value-object/GanttParameterVO'
import { GanttColor } from '../../../../../../../../../styles/commonStyles'
import { useProgressLinePoints } from './useProgressLinePoints'

type ProgressLineOverlayProps = {
  gridApi: GridApi
  timeScale: CalendarDateVO[]
  aggregateTarget: AggregateTarget
  aggregateField: AggregateField
  unit: GanttDisplayUnit
}

export const ProgressLineOverlay = ({
  gridApi,
  timeScale,
  aggregateTarget,
  aggregateField,
  unit,
}: ProgressLineOverlayProps) => {
  const [containerEl, setContainerEl] = useState<Element | null>(null)
  const [containerHeight, setContainerHeight] = useState(0)

  useEffect(() => {
    const viewport = document.querySelector('.ag-center-cols-viewport')
    setContainerEl(viewport)
  }, [])

  useEffect(() => {
    if (!containerEl) return
    containerEl.classList.add('has-progress-line')
    return () => containerEl.classList.remove('has-progress-line')
  }, [containerEl])

  useEffect(() => {
    if (!containerEl) return
    const inner = containerEl.querySelector('.ag-center-cols-container')
    if (!inner) return

    const updateHeight = () => {
      setContainerHeight(inner.scrollHeight)
    }
    updateHeight()

    const observer = new ResizeObserver(() => updateHeight())
    observer.observe(inner)
    return () => observer.disconnect()
  }, [containerEl])

  const ganttColumn = gridApi.getColumn('ganttChart')
  const ganttColumnLeft = ganttColumn?.getLeft() ?? 0
  const ganttColumnWidth = ganttColumn?.getActualWidth() ?? 0

  const { points, firstRowTop, lastRowBottom } = useProgressLinePoints(
    gridApi,
    timeScale,
    ganttColumnWidth,
    aggregateTarget,
    aggregateField,
    'body',
    unit
  )

  if (!containerEl || points.length === 0 || ganttColumnWidth <= 0) {
    return null
  }

  const first = points[0]
  const last = points[points.length - 1]
  const extendedPoints = [
    { x: first.x, y: firstRowTop },
    ...points,
    { x: last.x, y: lastRowBottom },
  ]

  const svgStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: ganttColumnLeft,
    width: ganttColumnWidth,
    height: containerHeight,
    pointerEvents: 'none',
    zIndex: 1,
    overflow: 'hidden',
  }

  return createPortal(
    <svg style={svgStyle}>
      <polyline
        points={extendedPoints.map(p => `${p.x},${p.y}`).join(' ')}
        fill="none"
        stroke={GanttColor.BORDER_TODAY}
        strokeWidth={3}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {points.map((p, i) => (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r={2}
          fill={GanttColor.BORDER_TODAY}
        />
      ))}
    </svg>,
    containerEl
  )
}
